// Admin per-user detail page — one payload from /admin/users/{id}/detail.
// Renders: identity + subscription + activity counts + feedback history.
// Wired from AdminUsers row "View" button.
import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

const STAT_LABELS = {
  funding_sources: "Accounts",
  bills: "Bills",
  incomes: "Income rows",
  snapshots: "Snapshots shared",
  pay_periods: "Pay periods",
  feedback: "Feedback rows",
};

export default function AdminUserDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/users/${id}/detail`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  if (user && user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
  if (loading) return <div className="text-sm text-[#8A918D]">Loading…</div>;
  if (!data) return <div className="text-sm text-[#8A918D]">User not found.</div>;

  const { user: target, subscription: sub, counts, feedback } = data;

  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      <div className="flex items-center justify-between gap-3">
        <Link to="/app/admin/users" className="btn-ghost text-sm" data-testid="admin-user-detail-back">
          <ChevronLeft size={14} /> Back to users
        </Link>
        <button onClick={load} className="btn-secondary text-sm">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="card-soft p-6">
        <div className="text-xs uppercase tracking-widest text-[#8A918D] mb-1">User</div>
        <h1 className="font-heading text-3xl font-semibold text-[#2C302E]">{target.full_name || target.email}</h1>
        <div className="text-sm text-[#59605C] mt-1">{target.email}</div>
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          <span className="px-2 py-0.5 rounded-full bg-[#F2EFEB] text-[#59605C]">Role: {target.role || "user"}</span>
          <span className="px-2 py-0.5 rounded-full bg-[#F2EFEB] text-[#59605C]">Signed up: {formatDate(target.created_at)}</span>
          <span className="px-2 py-0.5 rounded-full bg-[#F2EFEB] text-[#59605C]">Last login: {target.last_login_at ? formatDate(target.last_login_at) : "Never"}</span>
          <span className="px-2 py-0.5 rounded-full bg-[#F2EFEB] text-[#59605C]">Onboarded: {target.onboarding_completed ? "Yes" : "No"}</span>
          {target.suspended && (
            <span className="px-2 py-0.5 rounded-full bg-[#FBF3E0] text-[#8A6A1A]">Suspended</span>
          )}
        </div>
      </div>

      <div className="card-soft p-6">
        <h2 className="font-heading text-lg font-medium text-[#2C302E] mb-4">Activity</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(STAT_LABELS).map(([k, label]) => (
            <div key={k} className="rounded-2xl border border-[#D8CFB9] bg-white p-3" data-testid={`admin-user-detail-count-${k}`}>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">{label}</div>
              <div className="font-heading text-2xl font-semibold text-[#2C302E] mt-0.5">{counts[k] ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
          <span className="px-2 py-0.5 rounded-full bg-[#F2EFEB] text-[#59605C]">
            Reconciliation streak: {target.reconciliation_streak || target.balance_edit_streak || 0}
          </span>
          {target.last_reconciled_at && (
            <span className="px-2 py-0.5 rounded-full bg-[#F2EFEB] text-[#59605C]">
              Last reconciled: {formatDate(target.last_reconciled_at)}
            </span>
          )}
        </div>
      </div>

      <div className="card-soft p-6">
        <h2 className="font-heading text-lg font-medium text-[#2C302E] mb-4">Subscription</h2>
        <div className="text-sm text-[#59605C]">
          <div>Status: <strong className="text-[#2C302E]">{sub.status || "none"}</strong></div>
          {sub.trial_start && <div>Trial start: {formatDate(sub.trial_start)}</div>}
          {sub.trial_end && <div>Trial end: {formatDate(sub.trial_end)}</div>}
          {sub.stripe_customer_id && <div className="text-[11px] text-[#8A918D] mt-1">Stripe customer: {sub.stripe_customer_id}</div>}
        </div>
      </div>

      <div className="card-soft p-6">
        <h2 className="font-heading text-lg font-medium text-[#2C302E] mb-4">
          Feedback ({feedback.length})
        </h2>
        {feedback.length === 0 ? (
          <div className="text-sm text-[#8A918D]">No feedback from this user yet.</div>
        ) : (
          <ul className="space-y-3">
            {feedback.map((f) => (
              <li key={f.id} className="rounded-xl border border-[#EBE6E0] p-3 text-sm" data-testid={`admin-user-detail-fb-${f.id}`}>
                <div className="text-[11px] text-[#8A918D] mb-1">{formatDate(f.created_at)} · {f.category || "general"}</div>
                <div className="text-[#2C302E] whitespace-pre-wrap">{f.message}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
